import React, { useEffect, useMemo } from "react";
import { useAggrMetricsAcrossDimensions } from "../WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { useAggrEventFlows } from "./queries/useAggrEventFlows";
import { WIDGET_ELEM_ConfigForm } from "./ConfigForm";
import { transformHierarchyToSankey } from "./transformHierarchyToSankey";
import {
  WidgetErrorState,
  WidgetLoadingState,
} from "../../WidgetRenderer/comps/altStatePlaceholders";
import { annoDimForRequest } from "../utils/annoDimForRequest";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../ReportLandingContext";
import { SankeyChart } from "@/components/dataviz/SankeyChart";

const normalizeDisplayValue = (displayVal) => {
  if (Array.isArray(displayVal)) return displayVal[0] || "";
  return displayVal || "";
};

/**
 * Prune hierarchy by removing hidden events at each step level.
 */
const filterHierarchyByVisibility = (data, hiddenEventsPerStep) => {
  if (!hiddenEventsPerStep || Object.keys(hiddenEventsPerStep).length === 0)
    return data;

  const filterLevel = (nodes, step) => {
    const hiddenAtStep = hiddenEventsPerStep[String(step)] || [];

    let filtered = nodes;
    if (hiddenAtStep.length > 0) {
      filtered = nodes.filter((node) => {
        const display = normalizeDisplayValue(node.display);
        return !hiddenAtStep.includes(display);
      });
    }

    return filtered.map((node) => ({
      ...node,
      children: node.children?.length
        ? filterLevel(node.children, step + 1)
        : node.children,
    }));
  };

  return filterLevel(data, 0);
};

/**
 * Default "top flow" hidden list: hide all root events except the one with highest count.
 */
const getDefaultTopFlowHidden = (data) => {
  if (!data?.length || data.length <= 1) return {};
  const sorted = [...data].sort((a, b) => (b.count || 0) - (a.count || 0));
  return {
    "0": sorted.slice(1).map((n) => normalizeDisplayValue(n.display)),
  };
};

export const Component = ({
  contentType,
  dimensions,
  eventFlowConfig,
  efHiddenEventsPerStep,
  mode = "dimensionalCrossover",
  id,
  widgetIdx = 0,
  activeFilters = [],
  presetActiveFilters = [],
  widgetFilters = [],
  style = {},
  enableVisibilityFetching = false,
  prefetchedData,
}) => {
  const isEventFlow = mode === "sequentialEventFlow";
  const { updateWidgetDataStore } = useReportLandingContext() || {};


  const combinedFilters = useMemo(() => {
    return [...activeFilters, ...presetActiveFilters, ...widgetFilters];
  }, [activeFilters, presetActiveFilters, widgetFilters]);

  // Dimensional crossover hook (disabled when in event flow mode)
  const dimensionalResult = useAggrMetricsAcrossDimensions({
    vizCardId: id,
    contentType,
    dimensions,
    activeFilters: combinedFilters,
    metrics: [{ type: "count", name: "count" }],
    includePartialDimensions: true,
    enableVisibilityFetching,
    queryOptions: { enabled: !isEventFlow && !prefetchedData },
  });

  // Event flow hook (disabled when in dimensional mode)
  const eventFlowResult = useAggrEventFlows({
    vizCardId: id,
    contentType: eventFlowConfig?.contentType,
    idValuePath: eventFlowConfig?.idValuePath,
    eventNameValuePath: eventFlowConfig?.eventNameValuePath,
    orderIdxValuePath: eventFlowConfig?.orderIdxValuePath,
    timeValuePath: eventFlowConfig?.timeValuePath,
    activeFilters: combinedFilters,
    enableVisibilityFetching,
    queryOptions: { enabled: isEventFlow && !prefetchedData },
  });

  const {
    data: dataFromApiCall,
    status: statusFromApiCall,
    error,
    ref,
  } = isEventFlow ? eventFlowResult : dimensionalResult;

  const data = prefetchedData || dataFromApiCall;
  const status = prefetchedData ? "success" : statusFromApiCall;

  useUpdateLoadedWidgetsTracker({ status, widgetIdx });

  // Store raw data in context so ConfigForm visibility controls can read it
  useEffect(() => {
    if (status === "success" && data?.length) {
      updateWidgetDataStore && updateWidgetDataStore(id, data);
    }
  }, [data, status, id, updateWidgetDataStore]);

  const sankeyData = useMemo(() => {
    if (!data?.length) return null;

    let filtered = data;
    if (isEventFlow) {
      // efHiddenEventsPerStep === undefined → never configured → apply default top flow
      // efHiddenEventsPerStep === {} → explicitly "All" → no filtering
      // efHiddenEventsPerStep === { "0": [...] } → explicit config → apply it
      const hidden =
        efHiddenEventsPerStep !== undefined
          ? efHiddenEventsPerStep
          : getDefaultTopFlowHidden(data);
      filtered = filterHierarchyByVisibility(data, hidden);
    }

    return transformHierarchyToSankey(filtered);
  }, [data, isEventFlow, efHiddenEventsPerStep]);

  return (
    <div ref={ref}>
      {status === "loading" ? (
        <WidgetLoadingState />
      ) : status === "error" ? (
        <WidgetErrorState error={error} />
      ) : (
        status === "success" && sankeyData && (
          <SankeyChart
            data={sankeyData}
            colorIdx={widgetIdx % 4}
            style={style}
          />
        )
      )}
    </div>
  );
};

export const optionDef = {
  display: "Sankey Chart",
  icon: "FlowArrow",
  value: "sankeyChart",
  comp: "WidgetSankeyChart",
};

export const defaultConfig = { props: {} };

/**
 * Builds the API-ready config from the config form's raw state.
 * Handles both "dimensionalCrossover" and "sequentialEventFlow" modes.
 */
// A dimension slot with a real pick in it: a field, a rollup, or a Topics
// dimension (which carries neither a valuePath nor a rollupType).
const isConfiguredDim = (d) =>
  !!d && !!(d.valuePath || d.rollupType || d.dimensionType === "topicClusteringNode");

export const processRawConfigFn = (_widgetConfig) => {
  const mode = _widgetConfig.props?.mode || "dimensionalCrossover";

  if (mode === "sequentialEventFlow") {
    const efc = _widgetConfig.props?.eventFlowConfig;
    if (!efc?.eventNameField?.contentType) return _widgetConfig;

    return {
      ..._widgetConfig,
      props: {
        ..._widgetConfig.props,
        contentType: efc.eventNameField.contentType,
        eventFlowConfig: {
          contentType: efc.eventNameField.contentType,
          idValuePath: efc.idField?.valuePath,
          eventNameValuePath: efc.eventNameField?.valuePath,
          orderIdxValuePath:
            efc.orderByMode === "orderIdx"
              ? efc.orderField?.valuePath
              : undefined,
          timeValuePath:
            efc.orderByMode === "timestamp"
              ? efc.timeField?.valuePath
              : undefined,
        },
      },
    };
  }

  // --- Dimensional crossover (existing logic) ---
  const rawDims = (_widgetConfig.props?.dimensions || []).filter(isConfiguredDim);

  if (rawDims.length === 0) return _widgetConfig;

  let contentType = rawDims.some((d) => d.isAnno)
    ? "annotations"
    : rawDims[0].contentType;

  const dimensions = [];
  let presetActiveFilters = [];

  rawDims.forEach((dim) => {
    if (!dim.rollupType) {
      if (contentType === "annotations") {
        // Topics dimensions have no valuePath at all (see annoDimForRequest).
        if (dim.dimensionType === "topicClusteringNode" || dim.valuePath?.startsWith("annotations.")) {
          dimensions.push(annoDimForRequest(dim));
        } else {
          dimensions.push({
            contentType: dim.contentType,
            valuePath: dim.valuePath,
          });
        }
      } else {
        if (dim.isExpression) {
          dimensions.push({
            valuePath: dim.valuePath,
            isExpression: true,
          });
        } else {
          dimensions.push(dim.valuePath);
        }
      }
    } else if (dim.rollupType === "tagType") {
      dimensions.push({
        rollupType: dim.rollupType,
        path: dim.rollupPath,
        ...(dim.isAnno ? { isAnno: dim.isAnno } : {}),
        // Restrict the group-by buckets to a specific set of tag/sub-theme ids
        // (folder-level annotation select). Backend applies a post-rollup $match.
        ...(dim.restrictBucketValues ? { restrictBucketValues: dim.restrictBucketValues } : {}),
      });
    } else if (dim.rollupType === "valuePathType") {
      dimensions.push({
        rollupType: dim.rollupType,
        path: [dim.tagTypeCollectionToRollup, dim.blockValuePath],
        ...(dim.isAnno ? { isAnno: dim.isAnno } : {}),
      });
    }

    if (dim.presetActiveFilters) {
      presetActiveFilters = [...presetActiveFilters, ...dim.presetActiveFilters];
    }
  });

  return {
    ..._widgetConfig,
    props: {
      ..._widgetConfig.props,
      contentType,
      dimensions,
      presetActiveFilters,
    },
  };
};

export const ConfigForm = (props) => (
  <WIDGET_ELEM_ConfigForm
    {...props}
    widgetIcon={optionDef.icon}
    widgetTitle={optionDef.display}
  />
);

export const someReqPropsUnset = (widgetConfig) => {
  const mode = widgetConfig?.props?.mode || "dimensionalCrossover";

  if (mode === "sequentialEventFlow") {
    const efc = widgetConfig?.props?.eventFlowConfig;
    return (
      !efc?.eventNameField?.valuePath ||
      !efc?.idField?.valuePath ||
      (!efc?.orderField?.valuePath && !efc?.timeField?.valuePath)
    );
  }

  // Dimensional crossover: need at least 2 configured dimensions
  const dims = widgetConfig?.props?.dimensions || [];
  return dims.filter(isConfiguredDim).length < 2;
};
